/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Component from 'can/component/component';
import template from './social-share.stache';
import popupView from './popup.stache';
import appConfig from 'scripts/app-config';
import urlUtil from 'scripts/utils/url';
import deviceUtil from 'scripts/utils/device';
import can from 'can';
import _ from 'lodash';
import SocialShareService from 'scripts/services/social-share-service';
import dtm from 'scripts/services/dtm';
import DomEventsManager from 'scripts/dom-events-manager';

const TWITTER_MESSAGE_MAX_LENGTH = 140;
const MIN_FIELD_LENGTH = 30;

Component.extend({
    tag: 'social-share',
    template: template,
    viewModel: {
        hasHash: true,
        pagePath: '',
        title: '',
        image: '',
        description: '',
        stickToIcon: false,

        define: {
            inWebView: {
                get() {
                    return _.get(window, 'globalSettings.inWebView', false);
                }
            },

            appShareUrl: {
                get() {
                    let variables = this.getVariables();

                    return can.sub(appConfig.urls.share.app, variables);
                }
            },

            icon: {
                type: 'string',
                value: 'icon-share',
            },
        },

        getVariables: function () {
            let pagePath = (this.pagePath && String(this.pagePath)) || '';
            const url = urlUtil.getUrlFromPagePath(pagePath, this.hasHash);
            return {
                rawTitle: this.title,
                title: encodeURIComponent(this.title),
                /* It's used by some restrictive networks. */
                text: this.title,
                image_url: this.image || '',
                rawDescription: this.description,
                description: encodeURIComponent(this.description),
                rawUrl: url,
                url: encodeURIComponent(url)
            };
        },

        getEmailPath: function () {
            const variables = this.getVariables();

            return `mailto:?body=${variables.url}`;
        },

        getCroppedMessage: function (variables) {
            let message = ['', '', variables.rawUrl];
            /* Using of safe minimum value. Facebook doesn't have such limit so it will work.*/
            let length = TWITTER_MESSAGE_MAX_LENGTH - message[2].length;
            let trunkSeparator = /,? +/;

            if (length > MIN_FIELD_LENGTH && variables.rawTitle) {
                message[0] = _.trunc(variables.rawTitle, {
                    length: length - 1,
                    separator: trunkSeparator
                });
                length -= message[0].length;
            }

            if (length > MIN_FIELD_LENGTH && variables.rawDescription) {
                message[1] = _.trunc(variables.rawDescription, {
                    length: length - 1,
                    separator: trunkSeparator
                });
            }

            return message.join(' ');
        },

        open: function (ev, element) {
            ev.preventDefault();

            let $body = can.$('body');
            let viewModel = this;

            if (viewModel.$popup) {
                viewModel.$popup.remove();
            }

            $body.append(popupView(new can.Map({
                copied: false,
                title: this.title,
                popupClassName: this.popupClassName,
                destroy: () => {
                    if (viewModel.$popup) {
                        viewModel.$popup.remove();
                    }
                },

                share: (network, event, popup) => this.share(network, event, popup),

                inserted: function (el) {

                    viewModel.$popup = can.$(el);
                    viewModel.$popup.toggleClass('is-visible', true);
                    if (viewModel.stickToIcon && !viewModel.isMobile) {
                        let rect = element
                            .querySelector('.s-social-share__icon')
                            .getBoundingClientRect();
                        let popupContent = viewModel.$popup.find(
                            '.s-social-share-popup__content',
                        );
                        popupContent.css({
                            left:
                                rect.left +
                                rect.width * 2 -
                                popupContent.width() / 2,
                            top:
                                rect.top +
                                rect.height * 2 +
                                popupContent.height() / 2,
                        });
                    }
                },

                close: function () {
                    this.destroy();
                }
            })));
        },

        openDefaultEmailClient() {
            window.location.href = this.getEmailPath();
        },

        share: function (network, event, popup) {
            event.stopPropagation();

            if (network) {
                let variables = this.getVariables();

                if (network === 'url') {
                    let selection = window.getSelection();
                    let range = document.createRange();
                    let fakeNode = can.$(
                        '<div class="s-social-share-fake" contenteditable="true">' + variables.rawUrl + '</div>');

                    this.$element.prepend(fakeNode);

                    fakeNode.focus();

                    range.selectNodeContents(fakeNode[0]);
                    selection.removeAllRanges();
                    selection.addRange(range);

                    document.execCommand('copy');

                    if (deviceUtil.isIE() && window.clipboardData && window.clipboardData.getData('Text') !== variables.rawUrl) {
                        this.attr('copied', false);
                    } else {
                        this.attr('copied', true);
                    }

                    if (popup) popup.attr('copied', true);

                    fakeNode.remove();
                } else if (network === 'email') {
                    this.openDefaultEmailClient();
                } else {
                    SocialShareService.share(network, variables);
                }
            }

            dtm.trackShare(network);
        }

    },
    events: {
        inserted: function ($el) {
            DomEventsManager.subscribe(this);
            this.viewModel.$element = $el;
            var self = this;
            $(document).on('pushState', function () {
                self.viewModel.attr('copied', false);
            })
        },
        removed() {
            DomEventsManager.unsubscribe(this);
        },
        onWindowResize() {
            if (this.viewModel.stickToIcon) {
                this.debouncedPosition(); //TODO refactor component to use only css positioning ASAP
            }
        },
        debouncedPosition: _.debounce(function() {
            if (!this.viewModel.isMobile && this.viewModel.$popup) {
                let rect = this.element
                    .find('.s-social-share__icon')
                    .get(0)
                    .getBoundingClientRect();
                let popupContent = this.viewModel.$popup.find(
                    '.s-social-share-popup__content',
                );
                popupContent.css({
                    left: rect.left + rect.width * 2 - popupContent.width() / 2,
                    top: rect.top + rect.height * 2 + popupContent.height() / 2,
                });
            }
        }, 0),
    }
});
